/* PTL → PTL Billing → Billing Terms.
   Per client DPH (diesel price hike, D17-D19) and surcharges (V16). Versioned: a change is a new version from an
   effective date; removing one is a soft delete. DPH counts whole steps above the base price once diesel is above the
   trigger. Surcharge types are managed here (Manage surcharge types: add, rename, stop from a date); each is a % of base freight, ₹ per
   shipment or ₹ per kg, with an optional minimum. Adding / removing needs PTL_BILLING. */
import React, { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  Switch, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useSnackbar } from 'notistack'
import PageTitle from '../../components/PageTitle'
import { useAddBillingTermsMutation, useBillingTermsQuery, useRemoveBillingTermsMutation, useSurchargeTypesQuery,
  useSurchargeTypeListQuery, useAgreementQuery } from '../../redux/ptlBillingApiSlice'
import MerchantPicker from './components/MerchantPicker'
import TermsCheckList from './components/TermsCheckList'
import AgreementCard from './components/AgreementCard'
import SurchargeTypesDialog from './components/SurchargeTypesDialog'
import { canBill, errText, fmtDate, fmtDateTime, safeMonth } from './billingUtils'

/** DPH % of freight at a diesel price: whole steps above the base, only above the trigger (same as the backend). */
export const dhpPct = (t, diesel) => {
  if (!t?.dhpInContract || diesel === '' || diesel == null) return 0
  const d = Number(diesel)
  if (!(d > Number(t.dhpTriggerPrice))) return 0
  const steps = Math.floor((d - Number(t.dhpBasePrice)) / Number(t.dhpStep))
  return steps > 0 ? steps * Number(t.dhpHikePct) : 0
}

export default function BillingTerms() {
  const [params, setParams] = useSearchParams()
  const merchantOid = params.get('merchant') || null
  const tab = params.get('tab') === 'check' ? 'check' : 'client'
  const checkMonth = safeMonth(params.get('month'))
  const [allMerchants, setAllMerchants] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [typesOpen, setTypesOpen] = useState(false)
  const [example, setExample] = useState('')
  const { enqueueSnackbar } = useSnackbar()
  const billing = canBill()

  const { data: terms = [], isFetching, error } = useBillingTermsQuery(merchantOid, { skip: !merchantOid })
  const [remove, { isLoading: removing }] = useRemoveBillingTermsMutation()
  const current = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10)
    return terms.find((t) => String(t.effectiveFrom).slice(0, 10) <= today) || null
  }, [terms])

  const doRemove = async (t) => {
    if (!window.confirm(`Remove the terms from ${fmtDate(t.effectiveFrom)}? Shipments picked up from that date will use the previous version (or show "terms missing").`)) return
    const res = await remove({ id: t.id, merchantOid })
    if (res.error) enqueueSnackbar(errText(res.error), { variant: 'error' })
    else enqueueSnackbar('Terms removed', { variant: 'success' })
  }

  return (
    <Box>
      <PageTitle>Billing Terms</PageTitle>
      <Tabs value={tab} onChange={(_e, v) => setParams(v === 'check' ? { tab: 'check' } : merchantOid ? { merchant: merchantOid } : {})} sx={{ mb: 2 }}>
        <Tab value='client' label='Client terms' sx={{ textTransform: 'none' }} />
        <Tab value='check' label='Terms check list' sx={{ textTransform: 'none' }} />
      </Tabs>
      {tab === 'check' && (
        <Card elevation={0} sx={{ border: 1, borderColor: 'divider', p: 2 }}>
          <TermsCheckList month={checkMonth} onMonth={(m) => setParams({ tab: 'check', month: m })} onOpenClient={(m) => setParams({ merchant: m })} />
        </Card>
      )}
      {tab === 'client' && <>
      <Card elevation={0} sx={{ mb: 2, border: 1, borderColor: 'divider' }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <MerchantPicker value={merchantOid} ptlOnly={!allMerchants} placeholder='Search client' sx={{ minWidth: 340 }}
            onChange={(m) => setParams(m ? { merchant: m } : {})} />
          <FormControlLabel control={<Switch checked={allMerchants} onChange={(e) => setAllMerchants(e.target.checked)} />}
            label='Include merchants not flagged as PTL' />
          <Box sx={{ flex: 1 }} />
          {billing && <Button variant='outlined' onClick={() => setTypesOpen(true)} sx={{ textTransform: 'none', mr: 1 }}>Manage surcharge types</Button>}
          <Button variant='contained' disabled={!merchantOid || !billing} onClick={() => setAddOpen(true)} sx={{ textTransform: 'none' }}>
            Add terms version
          </Button>
        </CardContent>
        <Typography sx={{ px: 2, pb: 2, fontSize: 12, color: 'text.secondary' }}>
          DPH is a % of base freight (freight after the minimum, before fuel surcharge). Surcharges (war and any other type added with
          Manage surcharge types) are each a % of base freight, ₹ per shipment or ₹ per kg, with an optional
          minimum; lost shipments get none. A stopped type is not charged on shipments picked up from its stop date. A shipment uses the version in force on its pickup date. Without terms, its line is blocked as "terms missing".{!billing && ' Adding or removing terms needs PTL_BILLING access.'}
        </Typography>
      </Card>

      {!merchantOid && <Alert severity='info'>Pick a client to see its terms and agreement.</Alert>}
      <AgreementCard merchantOid={merchantOid} />
      {error && <Alert severity='error'>{errText(error, 'Could not load the terms.')}</Alert>}

      {merchantOid && !error && (
        <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
          <CardContent>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Versions {isFetching && '(loading…)'}</Typography>
            {terms.length === 0 && !isFetching && <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>No terms yet for this client.</Typography>}
            {terms.length > 0 && (
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Effective from</TableCell><TableCell>DPH</TableCell><TableCell>Surcharges</TableCell>
                    <TableCell>Remarks</TableCell><TableCell>Added by</TableCell><TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {terms.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell>{fmtDate(t.effectiveFrom)} {current?.id === t.id && <Chip size='small' color='success' label='IN FORCE' sx={{ ml: 1 }} />}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{t.dhpInContract
                        ? `Base ₹${t.dhpBasePrice}, trigger ₹${t.dhpTriggerPrice}, +${t.dhpHikePct}% per ₹${t.dhpStep}` : <i>Not in contract</i>}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{(t.surcharges || []).length
                        ? t.surcharges.map((x) => <div key={x.typeCode}>{surchargeText(x)}</div>) : <i>None</i>}</TableCell>
                      <TableCell sx={{ fontSize: 13, maxWidth: 220 }}>{t.remarks || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{t.createdBy || '—'}<br />{fmtDateTime(t.createdAt)}</TableCell>
                      <TableCell align='right'>
                        {billing && <LoadingButton size='small' color='error' loading={removing} onClick={() => doRemove(t)} sx={{ textTransform: 'none' }}>Remove</LoadingButton>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {current?.dhpInContract && (
              <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField size='small' type='number' label='Try a diesel price (₹/L)' value={example} onChange={(e) => setExample(e.target.value)} />
                <Typography sx={{ fontSize: 13 }}>
                  {example === '' ? 'Shows the DPH % of freight for the version in force.'
                    : `DPH = ${dhpPct(current, example).toFixed(2)}% of base freight`
                      + (Number(example) > Number(current.dhpTriggerPrice)
                        ? ` (${Math.floor((Number(example) - Number(current.dhpBasePrice)) / Number(current.dhpStep))} whole steps of ₹${current.dhpStep} above ₹${current.dhpBasePrice})`
                        : ` (diesel not above the ₹${current.dhpTriggerPrice} trigger)`)}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      </>}
      {addOpen && <AddTermsDialog merchantOid={merchantOid} copyFrom={current} onClose={() => setAddOpen(false)} onManageTypes={() => setTypesOpen(true)} />}
      {typesOpen && <SurchargeTypesDialog onClose={() => setTypesOpen(false)} />}
    </Box>
  )
}

/** "War surcharge: 1.6% of base freight", "Toll: ₹50 per shipment (min ₹100)". */
const surchargeText = (x) => `${x.typeLabel}: ${x.method === 'PCT_FREIGHT' ? `${Number(x.rate)}% of base freight`
  : `₹${Number(x.rate)} ${x.method === 'PER_KG' ? 'per kg' : 'per shipment'}`}${x.minAmount != null ? ` (min ₹${Number(x.minAmount)})` : ''}`

/** A new version starts as a copy of the one in force, so only what changed needs typing. */
function AddTermsDialog({ merchantOid, copyFrom, onClose, onManageTypes }) {
  const { enqueueSnackbar } = useSnackbar()
  const [add, { isLoading }] = useAddBillingTermsMutation()
  const { data: typeData } = useSurchargeTypesQuery()
  const { data: typeList = [] } = useSurchargeTypeListQuery()
  const types = typeList
  const methods = typeData?.methods || []
  const { data: agreement } = useAgreementQuery(merchantOid, { skip: !merchantOid })
  const c = copyFrom || {}
  const [v, setV] = useState({ effectiveFrom: new Date().toISOString().slice(0, 10), dhpInContract: copyFrom ? !!c.dhpInContract : true,
    dhpBasePrice: c.dhpBasePrice ?? '', dhpTriggerPrice: c.dhpTriggerPrice ?? '', dhpStep: c.dhpStep ?? '', dhpHikePct: c.dhpHikePct ?? '', remarks: '',
    agreementStartDate: '', agreementExpiryDate: '' })
  // agreement dates start as the client's current ones (blank when it has none)
  React.useEffect(() => {
    if (agreement?.startDate) setV((x) => ({ ...x, agreementStartDate: x.agreementStartDate || agreement.startDate, agreementExpiryDate: x.agreementExpiryDate || agreement.expiryDate }))
  }, [agreement])
  const stoppedBy = (t) => t.stopFrom && v.effectiveFrom >= t.stopFrom
  const [sur, setSur] = useState((c.surcharges || []).map((x) => ({ typeCode: x.typeCode, method: x.method, rate: x.rate ?? '', minAmount: x.minAmount ?? '' })))
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value })
  const setRow = (i, k, val) => setSur(sur.map((x, j) => (j === i ? { ...x, [k]: val } : x)))
  const n = (x) => (x === '' || x == null ? null : Number(x))
  const submit = async () => {
    const body = { merchantOid, effectiveFrom: v.effectiveFrom, dhpInContract: v.dhpInContract, remarks: v.remarks || null,
      agreementStartDate: v.agreementStartDate || null, agreementExpiryDate: v.agreementExpiryDate || null,
      surcharges: sur.map((x) => ({ typeCode: x.typeCode, method: x.method, rate: n(x.rate), minAmount: n(x.minAmount) })) }
    if (v.dhpInContract) Object.assign(body, { dhpBasePrice: n(v.dhpBasePrice), dhpTriggerPrice: n(v.dhpTriggerPrice), dhpStep: n(v.dhpStep), dhpHikePct: n(v.dhpHikePct) })
    const res = await add(body)
    if (res.error) return enqueueSnackbar(errText(res.error), { variant: 'error' })
    enqueueSnackbar('Terms saved', { variant: 'success' })
    onClose()
  }
  return (
    <Dialog open onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>Add terms version</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '12px !important' }}>
        <TextField size='small' type='date' label='Effective from (pickup date)' InputLabelProps={{ shrink: true }} value={v.effectiveFrom} onChange={set('effectiveFrom')} />
        <FormControlLabel control={<Switch checked={v.dhpInContract} onChange={(e) => setV({ ...v, dhpInContract: e.target.checked })} />} label='DPH (diesel price hike) is in the contract' />
        {v.dhpInContract && (
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
            <TextField size='small' type='number' label='Base diesel price (₹/L)' value={v.dhpBasePrice} onChange={set('dhpBasePrice')} />
            <TextField size='small' type='number' label='Trigger price (₹/L)' value={v.dhpTriggerPrice} onChange={set('dhpTriggerPrice')} helperText='Can be the same as the base' />
            <TextField size='small' type='number' label='Step (every ₹ rise of)' value={v.dhpStep} onChange={set('dhpStep')} />
            <TextField size='small' type='number' label='Hike per step (% of base freight)' value={v.dhpHikePct} onChange={set('dhpHikePct')} />
          </Box>
        )}
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Surcharges</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
            Add each surcharge in the contract. Need a new one (e.g. CYS charge) or a new name?{' '}
            <Button size='small' onClick={onManageTypes} sx={{ textTransform: 'none', p: 0, minWidth: 0, verticalAlign: 'baseline' }}>Manage surcharge types</Button>
          </Typography>
          {sur.map((x, i) => (
            <Box key={i} sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: '1.4fr 1.4fr 0.8fr 0.8fr auto' }, mb: 1, alignItems: 'center' }}>
              <TextField select size='small' label='Type' value={x.typeCode} onChange={(e) => setRow(i, 'typeCode', e.target.value)} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}>
                <option value=''>Pick a type</option>
                {types.map((t) => <option key={t.code} value={`PTL_SURCHARGE_TYPE:${t.code}`}
                  disabled={stoppedBy(t) || sur.some((o, j) => j !== i && o.typeCode === `PTL_SURCHARGE_TYPE:${t.code}`)}>
                  {t.label}{t.stopFrom ? ` (stops from ${t.stopFrom})` : ''}</option>)}
                {x.typeCode && !types.some((t) => `PTL_SURCHARGE_TYPE:${t.code}` === x.typeCode) && <option value={x.typeCode}>{x.typeCode} (inactive)</option>}
              </TextField>
              <TextField select size='small' label='How it is charged' value={x.method} onChange={(e) => setRow(i, 'method', e.target.value)} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}>
                <option value=''>Pick</option>
                {methods.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </TextField>
              <TextField size='small' type='number' label={x.method === 'PCT_FREIGHT' ? 'Rate (%)' : 'Rate (₹)'} value={x.rate} onChange={(e) => setRow(i, 'rate', e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField size='small' type='number' label='Minimum ₹ (optional)' value={x.minAmount} onChange={(e) => setRow(i, 'minAmount', e.target.value)} InputLabelProps={{ shrink: true }} />
              <Button color='error' size='small' onClick={() => setSur(sur.filter((_, j) => j !== i))} sx={{ textTransform: 'none' }}>Remove</Button>
            </Box>
          ))}
          <Button size='small' onClick={() => setSur([...sur, { typeCode: '', method: 'PCT_FREIGHT', rate: '', minAmount: '' }])} sx={{ textTransform: 'none' }}>+ Add surcharge</Button>
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Agreement dates</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
            From the client's signed agreement. They drive the agreement badge (active / expiring / expired). Leave both empty if not known yet.
          </Typography>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
            <TextField size='small' type='date' label='Agreement start' InputLabelProps={{ shrink: true }} value={v.agreementStartDate} onChange={set('agreementStartDate')} />
            <TextField size='small' type='date' label='Agreement expiry' InputLabelProps={{ shrink: true }} value={v.agreementExpiryDate} onChange={set('agreementExpiryDate')} />
          </Box>
        </Box>
        <TextField size='small' label='Remarks' value={v.remarks} onChange={set('remarks')} multiline minRows={2} inputProps={{ maxLength: 300 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton variant='contained' loading={isLoading} onClick={submit}>Save</LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
